import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';

import { FirebaseError } from 'firebase/app';
import { take } from 'rxjs/operators';

import { ErrorStatusCodeEnum } from '@core/enums';
import { ErrorService } from '@core/services/error.service';
import { environment as env, environment } from '@environments/environment';

enum LogLevelEnum {
  trace = 0,
  debug = 1,
  information = 2,
  warning = 3,
  error = 4,
  critical = 5,
  none = 6,
}

const ARR_ERROR_CODES = [
  ErrorStatusCodeEnum.OK,
  ErrorStatusCodeEnum.UNKNOWN_ERROR,
  ErrorStatusCodeEnum.SERVICE_UNAVAILABLE,
  ErrorStatusCodeEnum.API_DEPRECATED,
  ErrorStatusCodeEnum.RESOURCE_NOT_FOUND,
  ErrorStatusCodeEnum.ERROR_AUTH,
  ErrorStatusCodeEnum.FORBIDDEN,
  ErrorStatusCodeEnum.TOKEN_EXPIRED,
  ErrorStatusCodeEnum.TOO_MANY_REQUESTS,
  ErrorStatusCodeEnum.NEED_TO_UPGRADE,
  ErrorStatusCodeEnum.TIMEOUT,
  ErrorStatusCodeEnum.DUPLICATE_REQUEST,
  ErrorStatusCodeEnum.LIMIT_EXCEEDED,
  ErrorStatusCodeEnum.BAD_REQUEST,
  ErrorStatusCodeEnum.USER_TEMPORARILY_BLOCKED,
  ErrorStatusCodeEnum.TOKEN_REFRESH_EXPIRED,
];

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);

  /**
   * handle client error
   */
  handleError(error: Error | HttpErrorResponse): void {
    if (!environment.production) {
      console.error(error);
    }
    if (ignoreErrors(error) || environment.isTestSite) {
      return;
    }
    console.log(error);
    const errorService = this.injector.get(ErrorService);
    const data: any = {
      version: env.appVersion,
      logLevel: LogLevelEnum.error,
      message: error.message,
      language: 'ru',
      routeUrl: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };

    if (error instanceof HttpErrorResponse) {
      data.message = error.statusText;
      data.data = error.message;
      data.logLevel = LogLevelEnum.critical;
    } else {
      data.data = error.stack;
    }

    errorService.send(data).pipe(take(1)).subscribe();
  }
}

/**
 * ignore unnecessary http error
 * ignore test version
 */
function ignoreErrors(error: Error | HttpErrorResponse | any | FirebaseError): boolean {
  if (error instanceof HttpErrorResponse && [0, 404, 502].includes(error.status)) {
    return true;
  }
  if (error.errorCode && ARR_ERROR_CODES.includes(error.errorCode)) {
    return true;
  }
  if (error.message && error.message.includes('FirebaseError')) {
    return true;
  }
  if (error.message && error.message.includes('ChunkLoadError')) {
    return true;
  }
  if (error.message && error.message.includes('Failed to fetch')) {
    return true;
  }
  if (error instanceof FirebaseError) {
    return true;
  }
  return !environment.production;
}
